// Device-side sync engine: pull → apply (fast-forward, field-level merge or conflict) → push.
// Works with any transport (HTTP to the reference server, in-process for tests, files for the
// no-server "tablet pack" bridge). Nothing is ever discarded silently:
//  - local changes stay in the durable outbox until the server acknowledges them;
//  - a rejected change (permission, validation) is kept in state 'rejected' for review;
//  - an edit of the same field on both sides becomes a conflict for the advisor (Local vs Cloud).
import { TABLE_NAMES, allRowsOf, isLocalTable, newId, normalizeDay, validateRow, type Changeset, type Dataset, type Put, type SyncConflict, type SyncOp, type TableName, type WorkspaceView } from '@vero/domain';
import { normalizeRow, type Workspace } from '@vero/store';
import { isCriticalField, isStepUpRefusal, type PulledOp, type PushResult, type WireOp } from './protocol.ts';

export interface SyncTransport {
  push(ops: WireOp[]): Promise<PushResult[]>;
  pull(since: number, limit?: number): Promise<{ ops: PulledOp[]; last: number; more: boolean }>;
}
export interface SyncReport { pulled: number; applied: number; merged: number; conflicts: number; pushed: number; rejected: number; skipped: number; offline?: boolean; error?: string;
  /** requirement 21: what the server said with its refusal (e.g. client_outdated → minSchema, minApp) */
  detail?: Record<string, unknown> | null }
export interface SyncState {
  lastSeq: number; lastSyncAt: string | null; leaseUntil: string | null; workspaceId: string | null; deviceId: string | null;
  /** pairing (kept in device-local metadata, encrypted when the vault is locked; never replicated) */
  serverUrl?: string | null; token?: string | null; userId?: string | null; role?: string | null; workspaceName?: string | null; email?: string | null;
  revoked?: boolean; seeded?: boolean; uploaded?: string[]; lastError?: string | null;
  /** requirement 21: the server needs a newer app before this device may sync again (its outbox is kept) */
  updateNeeded?: { minSchema: number | null; minApp: string | null } | null;
}

const META = 'sync.state';
export async function loadSyncState(ws: Pick<Workspace, 'adapter'>): Promise<SyncState> {
  return { lastSeq: 0, lastSyncAt: null, leaseUntil: null, workspaceId: null, deviceId: null, ...((await ws.adapter.getMeta<SyncState>(META)) ?? {}) };
}
export async function saveSyncState(ws: Pick<Workspace, 'adapter'>, patch: Partial<SyncState>) {
  await ws.adapter.setMeta(META, { ...(await loadSyncState(ws)), ...patch });
}

export const toWire = (o: SyncOp): WireOp => ({ opId: o.id, csId: o.csId, deviceId: o.deviceId, table: o.table, rowId: o.rowId, kind: o.kind as 'put' | 'delete', baseVersion: o.baseVersion, rowVersion: o.rowVersion, fields: (o.fields as string[]) ?? [], row: (o.row as Record<string, unknown>) ?? null, at: o.at, ...(o.stepUpGrant ? { grant: o.stepUpGrant } : {}) });
export const pendingOps = (v: WorkspaceView) => v.rows('sync_ops').filter((o) => o.state === 'pending' && o.deviceId !== 'remote').sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));
export const openConflicts = (v: WorkspaceView) => v.rows('sync_conflicts').filter((c) => c.status === 'open');
export const rejectedOps = (v: WorkspaceView) => v.rows('sync_ops').filter((o) => o.state === 'rejected');

function touchedOp(o: SyncOp, now: string, patch: Partial<SyncOp>): SyncOp { return { ...o, ...patch, updatedAt: now, version: o.version + 1 }; }
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Apply a page of remote operations in ONE local transaction (origin 'remote': no new outbox entries).
 * Returns what happened, for the report.
 */
export function applyRemote(v: WorkspaceView, ops: PulledOp[], now: string, opts: { recordIds?: boolean } = {}): { cs: Changeset; applied: number; merged: number; conflicts: number; skipped: number } {
  const puts: Put[] = [];
  const deletes: Changeset['deletes'] = [];
  let applied = 0, merged = 0, conflicts = 0, skipped = 0;
  // working state inside the page (several ops may touch one row)
  const rowState = new Map<string, Record<string, unknown> | null>();
  const key = (t: string, id: string) => `${t}\u0000${id}`;
  const current = (t: string, id: string) => (rowState.has(key(t, id)) ? rowState.get(key(t, id))! : ((v.get(t as TableName, id) as unknown as Record<string, unknown>) ?? null));
  const pendingFor = new Map<string, SyncOp[]>();
  for (const o of v.rows('sync_ops')) if (o.state === 'pending' && o.deviceId !== 'remote') pendingFor.set(key(o.table, o.rowId), [...(pendingFor.get(key(o.table, o.rowId)) ?? []), o]);
  const openConflict = new Set(openConflicts(v).map((c) => key(c.table, c.rowId)));
  const seen = new Set(opts.recordIds ? v.rows('sync_ops').filter((o) => o.deviceId === 'remote' || o.state === 'acked').map((o) => o.id) : []);

  for (const op of ops) {
    // v14: a table of a newer app (unknown here) is skipped, never a crash
    if (isLocalTable(op.table) || !(TABLE_NAMES as readonly string[]).includes(op.table) || seen.has(op.opId)) { skipped++; continue; }
    seen.add(op.opId);
    if (opts.recordIds) puts.push({ table: 'sync_ops', row: normalizeRow({ id: op.opId, createdAt: now, updatedAt: now, version: 1, csId: op.csId, table: op.table, rowId: op.rowId, kind: op.kind, baseVersion: op.baseVersion, rowVersion: op.rowVersion, fields: op.fields, at: op.at, deviceId: 'remote', state: 'acked', seq: op.seq ?? null }) } as unknown as Put);
    const k = key(op.table, op.rowId);
    const local = current(op.table, op.rowId);
    const pending = pendingFor.get(k) ?? [];
    if (op.kind === 'put') {
      const row = normalizeRow(op.row ?? {}) as Record<string, unknown>;
      if (!op.row || row.id !== op.rowId || validateRow(op.table as TableName, row).length) { skipped++; continue; }
      if (local && Number(local.version) >= op.rowVersion && !pending.length) { skipped++; continue; } // stale or already applied
      if (!pending.length && !openConflict.has(k)) {
        puts.push({ table: op.table, row } as unknown as Put); rowState.set(k, row); applied++; continue;
      }
      if (openConflict.has(k)) { // already waiting for the advisor: refresh the cloud side of the conflict
        const c = openConflicts(v).find((x) => x.table === op.table && x.rowId === op.rowId)!;
        puts.push({ table: 'sync_conflicts', row: { ...c, remoteRow: row, updatedAt: now, version: c.version + 1 } } as unknown as Put); conflicts++; continue;
      }
      if (!local) { // we deleted it locally (pending delete) but the cloud edited it
        puts.push(conflictRow(now, op.table, op.rowId, null, row, op.fields, 'deleted_locally', pending[0]?.id)); openConflict.add(k); conflicts++; continue;
      }
      const localFields = [...new Set(pending.flatMap((p) => (p.fields as string[]) ?? []))];
      const overlap = localFields.filter((f) => op.fields.includes(f) && !same(local[f], row[f]));
      if (overlap.length) {
        puts.push(conflictRow(now, op.table, op.rowId, local, row, overlap, overlap.some((f) => isCriticalField(op.table, f)) ? 'critical_field' : 'edited_both', pending[0]?.id));
        openConflict.add(k); conflicts++; continue;
      }
      // disjoint edits: cloud row + our pending field values, rebased on the cloud version
      const mergedRow: Record<string, unknown> = { ...row };
      for (const f of localFields) { if (local[f] === undefined) delete mergedRow[f]; else mergedRow[f] = local[f]; }
      mergedRow.version = op.rowVersion + 1;
      mergedRow.updatedAt = now;
      puts.push({ table: op.table, row: mergedRow } as unknown as Put); rowState.set(k, mergedRow);
      const [first, ...rest] = pending;
      puts.push({ table: 'sync_ops', row: touchedOp(first!, now, { baseVersion: op.rowVersion, rowVersion: op.rowVersion + 1, row: mergedRow, fields: localFields }) } as unknown as Put);
      for (const r of rest) deletes.push({ table: 'sync_ops', id: r.id });
      pendingFor.set(k, [{ ...first!, baseVersion: op.rowVersion, rowVersion: op.rowVersion + 1 }]);
      merged++;
    } else {
      if (!local) { skipped++; continue; }
      if (pending.length || openConflict.has(k)) { puts.push(conflictRow(now, op.table, op.rowId, local, { id: op.rowId, version: op.rowVersion, _deleted: true }, [], 'deleted_remotely', pending[0]?.id)); openConflict.add(k); conflicts++; continue; }
      deletes.push({ table: op.table as TableName, id: op.rowId }); rowState.set(k, null); applied++;
    }
  }
  return { cs: { action: 'sync.apply', summary: `${applied}+${merged}/${conflicts}`, puts, deletes }, applied, merged, conflicts, skipped };
}
function conflictRow(now: string, table: string, rowId: string, localRow: Record<string, unknown> | null, remoteRow: Record<string, unknown> | null, fields: string[], reason: string, opId?: string): Put {
  const row: SyncConflict = { id: newId(), createdAt: now, updatedAt: now, version: 1, table, rowId, localRow: localRow ?? undefined, remoteRow: remoteRow ?? undefined, fields, status: 'open', reason, opId: opId ?? null };
  return { table: 'sync_conflicts', row: normalizeRow(row) } as unknown as Put;
}

/**
 * Resolve a conflict field by field. The result is written as a new local change based on the cloud
 * version (so the server accepts it), the superseded outbox entries are removed, and the conflict
 * keeps both versions and the decision for the audit trail.
 */
export function resolveConflict(v: WorkspaceView, conflictId: string, choice: Record<string, 'local' | 'remote'> | 'local' | 'remote', now: string): { cs: Changeset; origin: 'local' | 'remote' } {
  const c = v.get('sync_conflicts', conflictId);
  if (!c || c.status !== 'open') throw new Error('sync.conflict_not_open');
  const local = (c.localRow ?? null) as Record<string, unknown> | null;
  const remote = (c.remoteRow ?? null) as Record<string, unknown> | null;
  const pick = (f: string) => (typeof choice === 'string' ? choice : choice[f] ?? 'remote');
  const puts: Put[] = [];
  const deletes: Changeset['deletes'] = v.rows('sync_ops').filter((o) => o.state === 'pending' && o.table === c.table && o.rowId === c.rowId).map((o) => ({ table: 'sync_ops' as const, id: o.id }));
  const syncBase: Record<string, number> = {};
  let resolution: Record<string, unknown>;
  let origin: 'local' | 'remote' = 'remote';
  if (c.reason === 'deleted_remotely') {
    if (pick('*') === 'local' && local) { // keep ours: resurrect on top of the tombstone
      const tomb = Number(remote?.version ?? local.version);
      const row = { ...local, version: Math.max(Number(local.version), tomb) + 1, updatedAt: now };
      puts.push({ table: c.table, row } as unknown as Put); syncBase[c.rowId] = tomb;
      resolution = { keep: 'local' }; origin = 'local';
    } else { deletes.push({ table: c.table as TableName, id: c.rowId }); resolution = { keep: 'remote' }; }
  } else if (c.reason === 'deleted_locally') {
    if (pick('*') === 'local') { // keep our delete: rebase it on the cloud version
      deletes.length = 0;
      const del = v.rows('sync_ops').find((o) => o.state === 'pending' && o.table === c.table && o.rowId === c.rowId && o.kind === 'delete');
      if (del) puts.push({ table: 'sync_ops', row: touchedOp(del, now, { baseVersion: Number(remote!.version), rowVersion: Number(remote!.version) + 1 }) } as unknown as Put);
      resolution = { keep: 'local' };
    } else { puts.push({ table: c.table, row: { ...remote! } } as unknown as Put); resolution = { keep: 'remote' }; }
  } else {
    const row: Record<string, unknown> = { ...remote! };
    for (const f of (c.fields as string[])) if (pick(f) === 'local') { if (local?.[f] === undefined) delete row[f]; else row[f] = local[f]; }
    // fields only we changed (not in conflict) are kept too
    const localOnly = Object.keys(local ?? {}).filter((f) => !(c.fields as string[]).includes(f) && !['version', 'updatedAt', 'createdAt'].includes(f) && !same(local![f], remote![f]));
    const pendingFields = new Set(v.rows('sync_ops').filter((o) => o.state === 'pending' && o.table === c.table && o.rowId === c.rowId).flatMap((o) => (o.fields as string[]) ?? []));
    for (const f of localOnly) if (pendingFields.has(f)) row[f] = local![f];
    if (same({ ...row, version: 0, updatedAt: 0 }, { ...remote!, version: 0, updatedAt: 0 })) {
      puts.push({ table: c.table, row: { ...remote! } } as unknown as Put); // cloud version everywhere: adopt it, nothing to send
    } else {
      row.version = Number(remote!.version) + 1;
      row.updatedAt = now;
      puts.push({ table: c.table, row } as unknown as Put);
      syncBase[c.rowId] = Number(remote!.version);
      origin = 'local';
    }
    resolution = typeof choice === 'string' ? { all: choice } : choice;
  }
  puts.push({ table: 'sync_conflicts', row: { ...c, status: 'resolved', resolvedAt: now, resolution: JSON.stringify(resolution), updatedAt: now, version: c.version + 1 } } as unknown as Put);
  return { cs: { action: 'sync.resolve', summary: `${c.table}`, puts, deletes, syncBase }, origin };
}

export class SyncClient {
  private ws: Workspace;
  private transport: SyncTransport;
  private running: Promise<SyncReport> | null = null;
  constructor(ws: Workspace, transport: SyncTransport) { this.ws = ws; this.transport = transport; }

  /** One full cycle; concurrent calls share the running cycle. */
  sync(): Promise<SyncReport> {
    if (!this.running) this.running = this.cycle().finally(() => { this.running = null; });
    return this.running;
  }

  private async cycle(): Promise<SyncReport> {
    const rep: SyncReport = { pulled: 0, applied: 0, merged: 0, conflicts: 0, pushed: 0, rejected: 0, skipped: 0 };
    try {
      for (let round = 0; round < 3; round++) {
        await this.pullAll(rep);
        const retry = await this.pushAll(rep);
        if (!retry) break;
      }
      await saveSyncState(this.ws, { lastSyncAt: new Date().toISOString() });
      await this.prune();
    } catch (e) {
      rep.offline = true;
      rep.error = String((e as Error)?.message ?? e);
      rep.detail = (e as { detail?: Record<string, unknown> | null })?.detail ?? null;
    }
    return rep;
  }

  /** Our own acknowledged ops are only needed until the pull cursor has passed them (to recognise their
   *  echo); after that they are pruned so the local journal stays small. File-pack records ('remote'
   *  device) are kept: they make re-importing the same pack harmless. */
  private async prune() {
    const st = await loadSyncState(this.ws);
    if (!st.lastSeq) return;
    const done = this.ws.view.rows('sync_ops').filter((o) => o.state === 'acked' && o.deviceId !== 'remote' && o.seq != null && o.seq <= st.lastSeq);
    if (!done.length) return;
    await this.ws.run(() => ({ action: 'sync.prune', summary: String(done.length), puts: [], deletes: done.map((o) => ({ table: 'sync_ops' as const, id: o.id })) }), { origin: 'remote' });
  }

  private async pullAll(rep: SyncReport) {
    let st = await loadSyncState(this.ws);
    for (let page = 0; page < 200; page++) {
      const r = await this.transport.pull(st.lastSeq, 500);
      rep.pulled += r.ops.length;
      if (r.ops.length) {
        let result: ReturnType<typeof applyRemote> | null = null;
        await this.ws.run((v, ctx) => { result = applyRemote(v, r.ops, ctx.now); return result.cs; }, { origin: 'remote' });
        rep.applied += result!.applied; rep.merged += result!.merged; rep.conflicts += result!.conflicts; rep.skipped += result!.skipped;
      }
      await saveSyncState(this.ws, { lastSeq: r.last });
      st = { ...st, lastSeq: r.last };
      if (!r.more) break;
    }
  }

  /** @returns true when another pull/push round is useful (conflicting versions on the server) */
  private async pushAll(rep: SyncReport): Promise<boolean> {
    const blocked = new Set(openConflicts(this.ws.view).map((c) => `${c.table}\u0000${c.rowId}`));
    const ops = pendingOps(this.ws.view).filter((o) => !blocked.has(`${o.table}\u0000${o.rowId}`));
    let retry = false;
    for (let i = 0; i < ops.length; i += 200) {
      const batch = ops.slice(i, i + 200);
      const results = await this.transport.push(batch.map(toWire));
      const byId = new Map(results.map((r) => [r.opId, r]));
      await this.ws.run((v, ctx) => {
        const puts: Put[] = [];
        for (const o of batch) {
          const r = byId.get(o.id);
          const cur = v.get('sync_ops', o.id);
          if (!r || !cur) continue;
          if (r.status === 'ok' || r.status === 'duplicate') { puts.push({ table: 'sync_ops', row: touchedOp(cur, ctx.now, { state: 'acked', seq: r.seq ?? null, row: undefined, stepUpGrant: null }) } as unknown as Put); rep.pushed++; }
          else if (r.status === 'conflict') retry = true; // a newer server version exists: pull, rebase or ask, then push again
          else {
            puts.push({ table: 'sync_ops', row: touchedOp(cur, ctx.now, { state: 'rejected', error: `${r.status}${r.error ? ': ' + r.error : ''}`, stepUpGrant: null }) } as unknown as Put); rep.rejected++;
            // v13: a policy deletion/restore the server refused (no, expired or used step-up grant): this device goes back to
            // the server's version of the policy, so it never shows a deletion (or a restore) that did not happen
            if (isStepUpRefusal(r) && r.serverRow && r.serverRow.id === o.rowId) puts.push({ table: o.table, row: normalizeRow(r.serverRow) } as unknown as Put);
          }
        }
        return { action: 'sync.ack', puts, deletes: [] };
      }, { origin: 'remote' });
    }
    return retry;
  }
}

/** Re-queue a rejected change after the cause was fixed (e.g. permission granted), or discard it deliberately. */
export function retryRejected(v: WorkspaceView, opId: string, now: string): Changeset {
  const o = v.get('sync_ops', opId);
  if (!o || o.state !== 'rejected') throw new Error('sync.not_rejected');
  return { action: 'sync.retry', puts: [{ table: 'sync_ops', row: touchedOp(o, now, { state: 'pending', error: null }) } as Put], deletes: [] };
}
export function discardRejected(v: WorkspaceView, opId: string): Changeset {
  const o = v.get('sync_ops', opId);
  if (!o || o.state !== 'rejected') throw new Error('sync.not_rejected');
  return { action: 'sync.discard', summary: `${o.table}/${o.rowId}`, puts: [], deletes: [{ table: 'sync_ops', id: opId }] };
}
/** leases: after `leaseUntil` without reconnecting, the device becomes read-only until it signs in again */
export function leaseExpired(state: Pick<SyncState, 'leaseUntil'>, now = new Date()): boolean {
  return !!state.leaseUntil && state.leaseUntil < now.toISOString();
}
export const syncDay = (iso: string | null) => (iso ? normalizeDay(iso.slice(0, 10)) : null);

/** Resolve with the right write origin (only a genuinely new version is queued for the server). */
export async function resolveAndApply(ws: Workspace, conflictId: string, choice: Parameters<typeof resolveConflict>[2]): Promise<void> {
  const origin = resolveConflict(ws.view, conflictId, choice, new Date().toISOString()).origin;
  await ws.run((v, ctx) => resolveConflict(v, conflictId, choice, ctx.now).cs, { origin });
}

/**
 * First pairing of a device that already holds data: queue every existing row once (base version 0),
 * so the server receives the full workspace. Rows already queued are skipped. Local tables are never sent.
 */
export function seedOutbox(v: WorkspaceView, deviceId: string, now: string): Changeset {
  const queued = new Set(v.rows('sync_ops').filter((o) => o.state === 'pending').map((o) => `${o.table}\u0000${o.rowId}`));
  const csId = newId();
  const puts: Put[] = [];
  for (const t of TABLE_NAMES) {
    if (isLocalTable(t)) continue;
    // v13: allRowsOf — soft-deleted policies (and what depends on them) are workspace data too
    for (const r of allRowsOf(v, t) as unknown as Record<string, unknown>[]) {
      if (queued.has(`${t}\u0000${r.id}`)) continue;
      puts.push({ table: 'sync_ops', row: normalizeRow({ id: newId(), createdAt: now, updatedAt: now, version: 1, csId, table: t, rowId: r.id, kind: 'put', baseVersion: 0, rowVersion: Number(r.version ?? 1), fields: Object.keys(r).filter((k) => !['id', 'createdAt', 'updatedAt', 'version'].includes(k)).sort(), row: r, at: now, deviceId, state: 'pending' }) } as unknown as Put);
    }
  }
  return { action: 'sync.seed', summary: String(puts.length), puts, deletes: [] };
}
/** key-order-independent JSON (rows read from different stores list their fields in different orders) */
const canon = (x: unknown): string => JSON.stringify(x, (_k, v) => (v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, (v as Record<string, unknown>)[k]])) : v)) ?? 'undefined';
/**
 * After a restore or import replaced the data of a PAIRED device: queue every row that is new or changed compared with
 * the data before (base version = the version this device had, 0 for a new row). Deletions are never queued — restoring
 * a backup does not remove records from the server or from other devices. A pending change for the same row is replaced
 * by the new one (it would carry stale content).
 */
export function queueReplacedRows(v: WorkspaceView, before: Dataset, deviceId: string, now: string): Changeset {
  const pending = new Map(v.rows('sync_ops').filter((o) => o.state === 'pending').map((o) => [`${o.table}\u0000${o.rowId}`, o]));
  const csId = newId();
  const puts: Put[] = [];
  const deletes: { table: TableName; id: string }[] = [];
  for (const t of TABLE_NAMES) {
    if (isLocalTable(t)) continue;
    const prev = new Map(((before[t] ?? []) as unknown as Record<string, unknown>[]).map((r) => [r.id as string, r]));
    for (const r of allRowsOf(v, t) as unknown as Record<string, unknown>[]) {
      const p = prev.get(r.id as string);
      if (p && canon(normalizeRow(p)) === canon(normalizeRow(r))) continue;
      const key = `${t}\u0000${r.id}`;
      const old = pending.get(key);
      const base = old ? Number(old.baseVersion) : p ? Number(p.version ?? 1) : 0;
      const rowVersion = Number(r.version ?? 1);
      if (rowVersion <= base) continue;
      if (old) deletes.push({ table: 'sync_ops', id: old.id });
      const fields = p ? Object.keys({ ...p, ...r }).filter((k) => !['id', 'createdAt', 'updatedAt', 'version'].includes(k) && canon(p[k]) !== canon(r[k])).sort()
        : Object.keys(r).filter((k) => !['id', 'createdAt', 'updatedAt', 'version'].includes(k)).sort();
      puts.push({ table: 'sync_ops', row: normalizeRow({ id: newId(), createdAt: now, updatedAt: now, version: 1, csId, table: t, rowId: r.id, kind: 'put', baseVersion: base, rowVersion, fields, row: r, at: now, deviceId, state: 'pending' }) } as unknown as Put);
    }
  }
  return { action: 'sync.queue_restore', summary: String(puts.length), puts, deletes };
}
/** Status summary for the UI. */
export function syncStatus(v: WorkspaceView) {
  return { pending: pendingOps(v).length, conflicts: openConflicts(v).length, rejected: rejectedOps(v).length };
}
